<script>
  import { useData } from "$utils/data.svelte.js";
  import {
    TOOL_TITLE,
    INSTRUCTION_TEXT,
    GLOSSARY_POPOVER_TEXT,
    LEFT_CHART_TITLE,
    SEGREGATION_OPTIONS
  } from "$utils/constants.js";
  import { BasicDropdown, IconSearch, Toggle} from "@urbaninstitute/dataviz-components";
  import SearchComboBox from "./SearchComboBox.svelte";
  import LeftChart from "./LeftChart.svelte";
  import { getTier } from "$utils/tier.svelte.js";

  let toolData = useData();
  let viewportWidth = $state(0);
  let segregationDropdownWidth = $derived(getTier(viewportWidth) === "mobile" ? 220 : 240);

</script>

<svelte:window bind:innerWidth={viewportWidth} />

<div class="left-side-page-layout">
  <h3 class="tool-title alt-h3">{TOOL_TITLE}</h3>

  <div class="instruction-text-box">
    <p class="instruction-text">{@html INSTRUCTION_TEXT}
      <!--This has to be styled as inline text but should be a semantic html button-->
      <button class="glossary-btn" onclick={() => (toolData.isGlossaryOpen = true)}
        ><IconSearch size={11} /><span class="glossary-btn-text">{GLOSSARY_POPOVER_TEXT}</span></button
      ></p>
  </div>

  <div class="bottom-chart-wrap">
    <h6>{LEFT_CHART_TITLE}</h6>
    <div class="ux-bar">
      <SearchComboBox
        items={toolData.districtDropdownData}
        bind:value={toolData.activeDistrictID}
        maxWidth={175}
      />
      <div class="dropdown-container">
        <BasicDropdown
          id="left-boundary-strength-segregation"
          placeholder={null}
          inlineLabel="Select a segregation metric"
          data={SEGREGATION_OPTIONS}
          bind:value={toolData.activeSegregationMetric}
          dropdownWidth={segregationDropdownWidth}
        />
      </div>
    </div>
    <!-- Always render the toggle slot so the layout below it stays put; when the district has no
         other same-state districts, hide the toggle's contents (visibility keeps the space). -->
    <div class="state-toggle" class:is-empty={!toolData.hasSameStateDistricts}>
      <Toggle
        label={`Highlight other districts in ${toolData.activeDistrictData?.State}`}
        labelPosition="left"
        bind:active={toolData.highlightSameState}
      />
    </div>
    <LeftChart />
  </div>
</div>

<style>
  .left-side-page-layout {
    min-height: auto;
    display: flex;
    flex-direction: column;
  }
  @media (min-width: 1160px) {
    .left-side-page-layout {
      min-height: 100%;
    }
  }

  .bottom-chart-wrap {
    margin-top: auto;
  }

  h3.alt-h3.tool-title {
    color: var(--color-gray-shade-darkest, #353535);
    font-family: var(--font-family-sans-alt);
    font-size: 36px;
    font-weight: 600;
    line-height: 44px;
    text-transform: none;
    margin-top: 30px;
    margin-bottom: 22px;
  }

  .instruction-text-box {
    background-color: rgba(255, 255, 255, 0.72);
    padding: 15px 18px;
    margin-bottom: 40px;
    p.instruction-text {
      font-size: var(--font-size-small);
      line-height: 150%;
    }
  }
  .glossary-btn {
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;
    display: inline-flex;
    flex-wrap: nowrap;
    gap: 5px;
    align-items: center;
    vertical-align: middle;
    span.glossary-btn-text {
      color: var(--color-blue);
      font-weight: var(--font-weight-bold);
      font-size: var(--font-size-small);
      white-space: nowrap;
    }
  }

  .ux-bar {
    display: flex;
    justify-content: space-between;
    align-items: end;
    margin-top: 8px;
    margin-bottom: 22px;
    flex-wrap: wrap;
    gap: 9px;
  }
  @media (min-width: 800px) {
    .ux-bar {
      flex-wrap: nowrap;
    }
  }
  .ux-bar > :global(*) {
    flex-shrink: 0;
  }


.dropdown-container {
  flex: 0 0 260px;
}
@media (max-width: 799px) {
  .dropdown-container {
    flex: 0 0 220px;
  }
}

/* Search bar grows to fill the free space, keeping the 9px gap to the dropdown */
.ux-bar :global(.combobox-wrapper) {
  flex: 1 1 auto;
  min-width: 0;
}
.ux-bar :global(.combobox-wrapper .relative) {
  width: 100%;
}
.ux-bar :global(.combobox-wrapper input) {
  width: 100% !important;
  max-width: 100%;
}
.state-toggle {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-top: 6px;
  /* Gap below the toggle before the chart — applied at all widths (mobile, tablet, desktop). */
  margin-bottom: 12px;
}

/* No same-state districts: keep the div (and its reserved height) but hide the toggle so the
   chart below doesn't shift. visibility:hidden preserves layout and disables interaction. */
.state-toggle.is-empty {
  visibility: hidden;
}

.state-toggle :global(button.container) {
  padding: 0;
  margin: 0;
}

.state-toggle :global(button.container > p.label) {
  white-space: nowrap;
  font-size: 16px;
  font-weight: var(--font-weight-bold);
  font-style: normal;
  color: black;
  margin-top: 6px;
  margin-bottom: 0px;
  /* When the label wraps (mobile), keep both lines left-aligned and tightly spaced
     instead of inheriting a centered / loosely-spaced treatment. */
  text-align: left;
  line-height: 1.2;
}

@media (max-width: 799px) {
  .state-toggle :global(button.container > p.label) {
    white-space: nowrap;
    font-size: 14px;
  }
  .state-toggle :global(button.container) {
    gap: 0.375rem;
  }
}

.state-toggle :global(button.container .toggle) {
width: 36px;
height: 18px;
border-radius: 9px;
margin-top: 6px;
/* Keep the switch at full size when the label is long/narrow — otherwise the flex
   layout shrinks the switch (squished oval) to fit the label. */
flex-shrink: 0;
}

.state-toggle :global(button.container .toggle .circle) {
width: 14px;
height: 14px;
border-radius: 7px;
transform: translate(2px, 1px);
}

.state-toggle :global(button.container[aria-checked="true"] .circle) {
transform: translate(18px, 1px);
}
</style>
